import React, { useState, useEffect, useCallback } from "react";
import {
  Grid,
  Button,
  Dialog,
  CardHeader,
  CardContent,
  Typography,
  Box,
  RadioGroup,
  FormControlLabel,
  Radio,
  CurrencyInput,
  TextFielDecimalNumbers,
  Loading,
  Paper,
  Divider,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Chip,
  Stack,
  TextField,
  Autocomplete,
  FormControl,
  DialogContent,
} from "..";
import {
  formatPrecisaoFloat,
  formatPrice,
  responseErros,
  unformatPrice,
  voltar,
} from "../../utils";
import { evaluate } from "mathjs";
import {
  notification,
  ParametrosAliquotaService,
  ProdutosService,
} from "../../services";
import { Precificacao } from "./entities";
import precificacaoValidator from "./middlewares/precificacao.validator";
import shortid from "shortid";

const constructExpression = (expressao, variaveis, parametro) => {
  const variableMap = {};
  variableMap["Parametro"] =
    parametro !== undefined && parametro !== null
      ? parseFloat(parametro) / 100
      : 0;
  Object.keys(variaveis || {}).forEach((key) => {
    const val =
      variaveis[key] !== undefined && variaveis[key] !== null
        ? Number(variaveis[key])
        : 0;
    variableMap[key] = formatPrecisaoFloat(val);
    if (key.startsWith("_")) {
      const cleanKey = key.substring(1);
      variableMap[cleanKey] = formatPrecisaoFloat(val);
    }
  });

  const keys = Object.keys(variableMap).sort((a, b) => b.length - a.length);

  if (keys.length === 0) return evaluate(expressao);
  const escapedKeys = keys.map((key) =>
    key.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  );
  const pattern = new RegExp(escapedKeys.join("|"), "g");
  const expressaoProcessada = expressao.replace(pattern, (match) => {
    const value = variableMap[match];
    return value < 0 ? `(${value})` : value.toString();
  });
  return expressaoProcessada;
};

const validarExpressao = (expressao, variaveis, parametro) => {
  try {
    const expressaoProcessada = constructExpression(
      expressao,
      variaveis,
      parametro
    );
    return evaluate(expressaoProcessada);
  } catch (error) {
    console.error("Erro ao avaliar expressão:", expressao, error);
    return 0;
  }
};

const BASECALCULOS = [
  { name: "PrCompra", key: "precoCompra", value: "Preço de Compra" },
  { name: "PrStandard", key: "precoStandard", value: "Preço Standard" },
  { name: "CustoMedio", key: "precoMedio", value: "Custo Médio" },
];

const ModalPrecificacao = ({
  open,
  setOpen,
  produtos,
  handleSubmitProduto,
  handleSubmitNfEntrada,
}) => {
  const [parametroPrecificacaoList, setParametroPrecificacaoList] = useState(
    []
  );
  const [precificacaoCalculada, setPrecificacaoCalculada] = useState({});
  const [logCalculos, setLogCalculos] = useState([]);
  const [inputValues, setInputValues] = useState({});
  const [tipoBuscaBaseVenda, setTipoBuscaBaseVenda] = useState(
    BASECALCULOS[0].name
  );
  const [loading, setLoading] = useState(false);
  const [expandedLog, setExpandedLog] = useState(false);
  const [compraParamValues, setCompraParamValues] = useState([]);
  const [vendaParamValues, setVendaParamValues] = useState([]);
  const [currentPage, setCurrentPage] = useState(0);
  const [produtosModificados, setProdutosModificados] = useState([]);
  const [editando, setEditando] = useState(false);

  const parametrosAliquotaService = new ParametrosAliquotaService();
  const produtosService = new ProdutosService();

  const currentItem = produtosModificados[currentPage] || null;

  useEffect(() => {
    if (open && produtos?.length) {
      const produtosCopiados = produtos.map((item) => ({
        ...item,
        aliquotas: item.aliquotas ? [...item.aliquotas] : [],
        precoCompra: item.precoCompra || 0,
        precoStandard: item.precoStandard || 0,
        precoMedio: item.precoMedio || 0,
        precoVenda: item.precoVenda || 0,
        lucroDesejado: item.lucroDesejado || 0,
        comissao: item.comissao || 0,
      }));
      setProdutosModificados(produtosCopiados);
      setCurrentPage(0);

      const produtoIds = produtos.map((p) => p.produtoId).filter(Boolean);
      if (produtoIds.length) {
        buscarParametrosPrecificacao({ produtoId: produtoIds });
      } else {
        buscarParametrosPrecificacao();
      }
    }
  }, [open, produtos]);

  useEffect(() => {
    if (open && currentItem && parametroPrecificacaoList?.length) {
      let initialInputs = {
        precoCompra: currentItem.precoCompra || 0,
        precoStandard: currentItem.precoStandard || 0,
        precoMedio: currentItem.precoMedio || 0,
        lucroDesejado: currentItem.lucroDesejado || 0,
        precoVenda: currentItem.precoVenda || 0,
        comissao: currentItem.comissao,
      };
      initialInputs = buscarAliquotasRefencia(initialInputs, currentItem);
      parametroPrecificacaoList.forEach((param) => {
        const produtoParam = currentItem.parametrosAliquotas?.find(
          (a) => a.estParametroAliquotaId === param.id
        );
        const aliquotaCurrentItem = currentItem.aliquotas?.length
          ? currentItem.aliquotas?.find(
              (a) => a.parametroAliquotaId === param.id
            )?.valor
          : 0;
        const valor =
          aliquotaCurrentItem ||
          produtoParam?.valor ||
          initialInputs[param.identificador];
        initialInputs[param.identificador] = valor || 0;
      });
      setInputValues(initialInputs);
      calcularPrecos(initialInputs, tipoBuscaBaseVenda);
    }
  }, [
    produtosModificados,
    parametroPrecificacaoList,
    open,
    tipoBuscaBaseVenda,
  ]);

  function buscarAliquotasRefencia(initialInputs, currentItem) {
    if (parametroPrecificacaoList?.length && currentItem) {
      parametroPrecificacaoList.map((param) => {
        const keys = Object.keys(currentItem);
        const includeReferencia = param.referencia
          ? keys.includes(param.referencia)
          : false;
        if (includeReferencia && currentItem[param.referencia] > 0) {
          initialInputs[param.identificador] = currentItem[param.referencia];
        }
      });
    }
    return initialInputs;
  }

  async function buscarParametrosPrecificacao(filtros = {}) {
    setLoading(true);
    try {
      const res = await parametrosAliquotaService.getAll(filtros);
      if (!res.isAxiosError) {
        const parametros = [
          ...(res.data.COMPRA || []),
          ...(res.data.VENDA || []),
        ];
        setParametroPrecificacaoList(parametros);
        if (!parametros.length) {
          buscarParametrosPrecificacao();
        }
      } else {
        responseErros(res);
      }
    } catch (error) {
      console.error("Erro ao buscar parâmetros:", error);
    } finally {
      setLoading(false);
    }
  }

  const calcularPrecos = useCallback(
    (currentInputs, tipoBuscaBase = "PrCompra") => {
      let tempPrecificacao = { ...currentInputs };
      const currentLog = [];

      let precoBaseParaVenda = 0;
      let precoBaseParaVendaLabel = "";
      switch (tipoBuscaBase) {
        case "PrCompra":
          precoBaseParaVenda = parseFloat(currentInputs.precoCompra) || 0;
          precoBaseParaVendaLabel = "Preço Compra";
          break;
        case "PrStandard":
          precoBaseParaVenda = parseFloat(currentInputs.precoStandard) || 0;
          precoBaseParaVendaLabel = "Preço Standard";
          break;
        case "CustoMedio":
          precoBaseParaVenda = parseFloat(currentInputs.precoMedio) || 0;
          precoBaseParaVendaLabel = "Custo Médio";
          break;
        default:
          precoBaseParaVenda = parseFloat(currentInputs.precoCompra) || 0;
          precoBaseParaVendaLabel = "Preço Compra";
          break;
      }

      const paramsCompra = parametroPrecificacaoList.filter(
        (p) => p.incidencia === "COMPRA"
      );
      const paramsVenda = parametroPrecificacaoList.filter(
        (p) => p.incidencia === "VENDA"
      );

      currentLog.push({ section: "Formação de Custo da Compra", steps: [] });
      let precoReferenciaCompraCalculado = precoBaseParaVenda;
      currentLog[0].steps.push(
        `Preço de Compra Inicial = ${formatPrice(
          precoReferenciaCompraCalculado
        )}`
      );

      const compraParamsAtivos = [];
      const parametrosComputados = {};

      const ordenarPorDependencias = (params) => {
        const ordenados = [];
        const processados = new Set();
        const emProcessamento = new Set();

        const processar = (param) => {
          if (processados.has(param.identificador)) return;
          if (emProcessamento.has(param.identificador)) {
            return;
          }

          emProcessamento.add(param.identificador);

          if (param.expressao) {
            const identificadoresNaExpressao =
              param.expressao.match(/[_a-zA-Z][a-zA-Z0-9]*/g) || [];

            for (const idExpr of identificadoresNaExpressao) {
              const paramDependente = params.find((p) => {
                const cleanId = p.identificador.startsWith("_")
                  ? p.identificador.substring(1)
                  : p.identificador;
                const cleanExpr = idExpr.startsWith("_")
                  ? idExpr.substring(1)
                  : idExpr;
                return cleanId === cleanExpr;
              });

              if (
                paramDependente &&
                !processados.has(paramDependente.identificador)
              ) {
                processar(paramDependente);
              }
            }
          }

          emProcessamento.delete(param.identificador);
          processados.add(param.identificador);
          ordenados.push(param);
        };

        params.sort((a, b) => a.ordem - b.ordem).forEach(processar);

        return ordenados;
      };

      const paramsCompraOrdenados = ordenarPorDependencias(paramsCompra);

      for (const param of paramsCompraOrdenados) {
        const valorParametro =
          parseFloat(tempPrecificacao[param.identificador]) || 0;
        const deveCalcular = param.calculoSemParametro || valorParametro > 0;
        if (deveCalcular && !param.removerIncidenciaDireta) {
          let calculatedAdditive = 0;
          let logMessage = "";
          const variablesForExpression = {
            PrCompra: parseFloat(currentInputs.precoCompra) || 0,
            PrStandard: parseFloat(currentInputs.precoStandard) || 0,
            PrVenda: parseFloat(currentInputs.precoVenda) || 0,
            CustoMedio: parseFloat(currentInputs.precoMedio) || 0,
            LucroDesejado: parseFloat(currentInputs.lucroDesejado) || 0,
            PrRef: precoBaseParaVenda,
            PrCusto: precoReferenciaCompraCalculado,
            ...currentInputs,
            ...parametrosComputados,
          };
          if (param.tipo === "DECIMAL") {
            calculatedAdditive = valorParametro;
            logMessage = `${param.descricao} (R$ ${formatPrice(
              valorParametro,
              false
            )}) = ${formatPrice(
              calculatedAdditive
            )} => Preço de Custo Parcial = ${formatPrice(
              precoReferenciaCompraCalculado + calculatedAdditive
            )}`;
          } else {
            calculatedAdditive = validarExpressao(
              param.expressao,
              variablesForExpression,
              valorParametro,
              param.tipo
            );

            if (param.calculoSemParametro) {
              const expressaoProcessada = constructExpression(
                param.expressao,
                variablesForExpression,
                valorParametro
              );
              const regex =
                /\(\(\s*([\d.]+)\s*-\s*([\d.]+)\s*\)\s*\/\s*100\s*\)/;
              const matchValues = expressaoProcessada.match(regex);
              if (matchValues) {
                const calc = matchValues[0].replace(/\/100\s*\)?/, ")");
                const resultadoCalc = evaluate(calc);
                currentInputs[param.identificador] =
                  formatPrecisaoFloat(resultadoCalc);
              }
            }

            logMessage = `${
              param.descricao
            } (${valorParametro}%) = ${formatPrice(
              calculatedAdditive
            )} => Preço de Custo Parcial = ${formatPrice(
              precoReferenciaCompraCalculado + calculatedAdditive
            )}`;
          }

          if (!isNaN(calculatedAdditive) && calculatedAdditive !== 0) {
            precoReferenciaCompraCalculado +=
              formatPrecisaoFloat(calculatedAdditive);

            parametrosComputados[param.identificador] = calculatedAdditive;

            if (param.identificador.startsWith("_")) {
              const cleanKey = param.identificador.substring(1);
              parametrosComputados[cleanKey] = calculatedAdditive;
            }

            compraParamsAtivos.push({
              label: param.descricao,
              value: calculatedAdditive,
              tipo: param.tipo,
            });
            currentLog[0].steps.push(logMessage);
          }
        }
      }

      setCompraParamValues(compraParamsAtivos);

      tempPrecificacao.precoCusto = parseFloat(
        precoReferenciaCompraCalculado.toFixed(2)
      );
      currentLog[0].steps.push(
        `Preço de Custo Final = ${formatPrice(tempPrecificacao.precoCusto)}`
      );

      currentLog.push({
        section: "Formação de Preço de Venda",
        steps: [],
      });

      currentLog[1].steps.push(
        `Preço de Referência Selecionado (${precoBaseParaVendaLabel}) = ${formatPrice(
          precoBaseParaVenda
        )}`
      );

      let totalSomaPorcentagem = 0;
      currentLog[1].steps.push(
        "Aplicando parâmetros de venda sobre o preço de referência:"
      );

      let precoVendaCalculado = 0;
      const incidenciaVenda = [
        ...paramsVenda,
        { descricao: "LUCRO", identificador: "lucroDesejado" },
        { descricao: "COMISSÃO", identificador: "comissao" },
      ];
      const { totalSomaPorcentagem: novoTotalPorcentagem, itens } =
        incidenciaVenda.reduce(
          (acc, param) => {
            const valorParametro =
              parseFloat(tempPrecificacao[param.identificador]) || 0;
            if (valorParametro <= 0) return acc;
            let lucro = parseFloat(tempPrecificacao.lucroDesejado);
            if (param.identificador === "lucroDesejado") {
              if (isNaN(lucro)) lucro = 0;
              if (lucro >= 100) {
                tempPrecificacao.lucroDesejado = 99.99;
                lucro = 99.99;
              }
            }
            const percentualDecimal =
              param.identificador === "lucroDesejado"
                ? lucro / 100
                : valorParametro / 100;

            acc.totalSomaPorcentagem += percentualDecimal;
            acc.itens.push({
              descricao: param.descricao,
              percentualDecimal,
            });

            return acc;
          },
          { totalSomaPorcentagem: 0, itens: [] }
        );
      totalSomaPorcentagem = novoTotalPorcentagem;

      if (precoBaseParaVenda <= 0) {
        currentLog[1].steps.push(
          `⚠️ ERRO: Preço de referência é R$ 0,00. Cálculo de venda não pode ser realizado.`
        );
        precoVendaCalculado = 0;
      } else {
        precoVendaCalculado =
          precoReferenciaCompraCalculado / (1 - totalSomaPorcentagem);
        currentLog[1].steps.push(
          `Fórmula: Preço Venda = Preço Referência / (1 - Soma Percentuais)`
        );
        currentLog[1].steps.push(
          `Cálculo: ${formatPrice(precoBaseParaVenda)} / 1 = ${formatPrice(
            precoVendaCalculado
          )}`
        );
      }
      tempPrecificacao.precoVenda = !isNaN(precoVendaCalculado)
        ? parseFloat(precoVendaCalculado.toFixed(2))
        : 0;
      const resultado = itens.map((it) => ({
        label: it.descricao,
        value: tempPrecificacao.precoVenda * it.percentualDecimal,
      }));
      setVendaParamValues(resultado);

      currentLog[1].steps.push(
        `Soma total dos percentuais: ${(totalSomaPorcentagem * 100).toFixed(
          4
        )}% (decimal: ${totalSomaPorcentagem.toFixed(6)})`
      );
      setPrecificacaoCalculada(tempPrecificacao);
      setLogCalculos(currentLog);
      return currentInputs;
    },
    [parametroPrecificacaoList]
  );

  const handleRadioChange = (event) => {
    const newTipoBusca = event.target.value;
    setTipoBuscaBaseVenda(newTipoBusca);
    calcularPrecos(inputValues, newTipoBusca);
  };

  const handleAliquotasUpdate = (item, precificacaoData) => {
    const aliquotasAtualizadas = [...(item.aliquotas || [])];
    const newAliquotaValues = parametroPrecificacaoList.reduce((acc, param) => {
      const value = precificacaoData[param.identificador] || 0;
      if (value > 0) {
        const aliquotaExistente = aliquotasAtualizadas.find(
          (a) => a.parametroAliquotaId === param.id
        );
        if (aliquotaExistente) {
          acc.push({ ...aliquotaExistente, valor: value });
        } else {
          acc.push({
            parametroAliquotaId: param.id,
            valor: value,
            id: shortid.generate(),
          });
        }
      }
      return acc;
    }, []);
    const aliquotasFinais = newAliquotaValues.concat(
      aliquotasAtualizadas.filter(
        (a) =>
          !parametroPrecificacaoList.some((p) => p.id === a.parametroAliquotaId)
      )
    );
    return aliquotasFinais;
  };

  // const handleCreatePrecificacaoDto = (item) => {
  //   const aliquotas = parametroPrecificacaoList.map((param) => {
  //     const valueFormated = parseFloat(inputValues[param.identificador]);
  //     const value =
  //       !isNaN(valueFormated) && valueFormated > 0 ? valueFormated : 0;
  //     const existente = item.parametrosAliquotas?.find(
  //       (a) => a.estParametroAliquotaId === param.id
  //     );

  //     return existente
  //       ? {
  //           ...existente,
  //           parametroAliquotaId: existente.estParametroAliquotaId,
  //           valor: value,
  //         }
  //       : {
  //           parametroAliquotaId: param.id,
  //           valor: value,
  //           id: shortid.generate(),
  //         };
  //   });
  //   if (
  //     handleSubmitProduto &&
  //     typeof handleSubmitProduto === "function" &&
  //     !item.produtoId
  //   ) {
  //     item.produtoId = 0;
  //   }
  //   const preco =  new Precificacao({
  //     ...item,
  //     precoCompra: inputValues.precoCompra,
  //     precoCusto: precificacaoCalculada.precoCusto,
  //     precoStandard: inputValues.precoStandard,
  //     precoMedio: inputValues.precoMedio,
  //     precoVenda: precificacaoCalculada.precoVenda,
  //     lucroDesejado: inputValues.lucroDesejado,
  //     aliquotas,
  //   });
  //   return preco;
  // };

  const formatAliquotasPrecificacaoItem = (item) => {
    return parametroPrecificacaoList.map((param) => {
      const valueFormated = parseFloat(inputValues[param.identificador]);
      const value =
        !isNaN(valueFormated) && valueFormated > 0 ? valueFormated : 0;
      const existente = item.parametrosAliquotas?.find(
        (a) => a.estParametroAliquotaId === param.id
      );

      return existente
        ? {
            ...existente,
            parametroAliquotaId: existente.estParametroAliquotaId,
            valor: value,
          }
        : {
            parametroAliquotaId: param.id,
            valor: value,
            id: shortid.generate(),
          };
    });
  };

  const onSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    const finalProdutosModificados = [...produtosModificados];
    const aliquotas = formatAliquotasPrecificacaoItem(
      finalProdutosModificados[currentPage]
    );
    finalProdutosModificados[currentPage] = {
      ...finalProdutosModificados[currentPage],
      ...inputValues,
      ...precificacaoCalculada,
      aliquotas,
    };

    const precificacoes = finalProdutosModificados.map(
      (item) => new Precificacao(item)
    );
    await cadastrarPrecificacao(precificacoes);
  };

  const cadastrarPrecificacao = async (precificacoes) => {
    try {
      await precificacaoValidator.validate(precificacoes, {
        abortEarly: false,
      });
      if (
        handleSubmitProduto &&
        typeof handleSubmitProduto === "function" &&
        precificacoes[0].produtoId === 0
      ) {
        handleSubmitProduto(precificacoes);
        notification.sucessoGenericos("Precificação realizada");
        setOpen(false);
        return { saved: false, data: precificacoes };
      }
      const res = await produtosService.createPrecificacao(precificacoes);
      if (!res.isAxiosError) {
        notification.alteracaoSucesso();
        if (
          handleSubmitNfEntrada &&
          typeof handleSubmitNfEntrada === "function"
        ) {
          handleSubmitNfEntrada(true);
          return { saved: true, data: res.data };
        } else {
          voltar();
        }
        setOpen(false);
      } else {
        responseErros(res);
        return { saved: false };
      }
    } catch (err) {
      console.error("Erro na validação:", err);
      return { saved: false, error: err };
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  const atualizarProdutoAtual = (dados) => {
    const novosProdutos = [...produtosModificados];
    novosProdutos[currentPage] = {
      ...novosProdutos[currentPage],
      ...dados,
    };
    setProdutosModificados(novosProdutos);
  };

  const handleChangeInput = useCallback(
    (name, value) => {
      setInputValues((prevInputs) => {
        const valuePrice = unformatPrice(value);
        let newInputs = { ...prevInputs, [name]: valuePrice };
        const inputs = calcularPrecos(newInputs, tipoBuscaBaseVenda);
        return inputs;
      });
    },
    [calcularPrecos, tipoBuscaBaseVenda]
  );

  const handleLogAccordionChange = (_, isExpanded) => {
    setExpandedLog(isExpanded);
  };

  const salvarEstadoAtual = () => {
    const produtoAtualizado = {
      ...produtosModificados[currentPage],
      ...inputValues,
      ...precificacaoCalculada,
      aliquotas: handleAliquotasUpdate(
        produtosModificados[currentPage],
        inputValues
      ),
    };
    atualizarProdutoAtual(produtoAtualizado);
  };

  const changePage = (nextPage) => {
    salvarEstadoAtual();
    setCurrentPage(nextPage);
  };

  const nextPage = () => {
    if (currentPage < produtosModificados.length - 1) {
      changePage(currentPage + 1);
    }
  };

  const prevPage = () => {
    if (currentPage > 0) {
      changePage(currentPage - 1);
    }
  };

  const handleAutocompleteChange = (e, newValue) => {
    if (!newValue) return;
    const selectedIndex = produtosModificados.findIndex(
      (item) => item.id === newValue.id
    );
    if (selectedIndex !== -1) {
      changePage(selectedIndex);
    }
  };

  function editPrecoVenda(e) {
    setEditando(false);
    const precoVenda = unformatPrice(e.target.value);
    const precoCusto = precificacaoCalculada.precoCusto;
    const incidenciasVenda = vendaParamValues
      .filter((v) => v.label !== "LUCRO")
      .reduce((acc, v) => acc + v.value, 0);
    const lucroReal = parseFloat(precoVenda) - incidenciasVenda - precoCusto;
    const lucroPercent = precoVenda
      ? formatPrecisaoFloat((lucroReal / parseFloat(precoVenda)) * 100)
      : 0;
    setVendaParamValues((prev) =>
      prev.map((item) =>
        item.label === "LUCRO" ? { ...item, value: lucroReal } : item
      )
    );
    setInputValues((prev) => ({
      ...prev,
      precoVenda,
      lucroDesejado: lucroPercent,
      precoCusto,
    }));
    setPrecificacaoCalculada((prev) => ({
      ...prev,
      precoVenda,
      lucroDesejado: lucroPercent,
      precoCusto,
    }));
  }

  const parametrosCompra = parametroPrecificacaoList.filter(
    (p) => p.incidencia === "COMPRA"
  );
  const parametrosVenda = parametroPrecificacaoList.filter(
    (p) => p.incidencia === "VENDA"
  );

  const temErroCalculo =
    precificacaoCalculada.precoVenda && precificacaoCalculada.precoVenda < 0;

  const Incidencias = ({ parametros, venda }) => {
    if (!parametros.length) return null;
    const element = document.getElementById(tipoBuscaBaseVenda);
    return (
      <>
        <Typography
          variant="body2"
          color="text.secondary"
          gutterBottom
          sx={{ fontWeight: 700 }}
        >
          {venda
            ? `Incidências na Venda:`
            : `Incidências no ${element?.textContent}`}
        </Typography>
        {parametros.map((param, idx) => (
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            key={idx}
          >
            <Typography variant="body2" sx={{ fontWeight: 500 }}>
              {param.label}
            </Typography>
            <Box
              sx={{
                borderBottom: "1px dotted grey",
                borderWidth: "0.170rem",
                flexGrow: "2",
                mx: "1rem",
              }}
            />
            <Typography variant="body2" sx={{ fontWeight: 600, mr: 1 }}>
              {param.value > 0
                ? `+ ${formatPrice(param.value, false)}`
                : `- ${formatPrice(param.value * -1, false)}`}
            </Typography>
          </Box>
        ))}
      </>
    );
  };

  return (
    <React.Fragment>
      <Dialog
        fullWidth
        maxWidth="xl"
        PaperComponent="div"
        PaperProps={{
          style: {
            borderRadius: "0.7rem",
            background: `#ffffff`,
          },
        }}
        open={open}
        onClose={handleClose}
      >
        <DialogContent>
          <CardHeader
            title="Precificação de Produtos"
            subheader={
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                {currentItem?.descricao}
              </Typography>
            }
            sx={{ padding: "1rem 1.6rem" }}
          />
          <CardContent sx={{ p: 3 }}>
            {produtosModificados?.length > 1 && (
              <Paper
                sx={{
                  p: 3,
                  border: "1px solid #e0e0e0",
                  borderRadius: 4,
                  backgroundColor: "#fafafa",
                  boxShadow: "none !important",
                  mb: 3,
                }}
              >
                <Box display="flex" alignItems="center" gap={1} mb={3}>
                  <i
                    className="ph ph-list"
                    style={{ fontSize: 20, color: "#1976d2" }}
                  />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Navegação de Itens ({currentPage + 1}/
                    {produtosModificados.length})
                  </Typography>
                </Box>

                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} md={6}>
                    <FormControl variant="outlined" fullWidth>
                      <Autocomplete
                        id="produtoId"
                        name="produtoId"
                        options={produtosModificados || []}
                        autoHighlight
                        loadingText="Carregando"
                        noOptionsText="Nenhum produto encontrado"
                        getOptionLabel={(option) =>
                          option.descricao ? option.descricao : ""
                        }
                        onChange={handleAutocompleteChange}
                        value={currentItem || null}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            variant="outlined"
                            label="Buscar produto"
                            size="small"
                            InputProps={{
                              ...params.InputProps,
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={prevPage}
                        disabled={currentPage === 0}
                        size="small"
                      >
                        <i
                          className="ph-fill ph-caret-left"
                          style={{ fontSize: 16, marginRight: 5 }}
                        />
                        Anterior
                      </Button>
                      <Button
                        variant="contained"
                        color="primary"
                        onClick={nextPage}
                        disabled={
                          currentPage + 1 === produtosModificados?.length
                        }
                        size="small"
                      >
                        Próximo
                        <i
                          className="ph-fill ph-caret-right"
                          style={{ fontSize: 16, marginLeft: 5 }}
                        />
                      </Button>
                    </Box>
                  </Grid>
                </Grid>

                {currentItem && (
                  <Box sx={{ mt: 2 }}>
                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={3}>
                        <TextField
                          label="Código"
                          value={currentItem.codigo || ""}
                          disabled
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={9}>
                        <TextField
                          label="Descrição"
                          value={currentItem.descricao || ""}
                          disabled
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Paper>
            )}

            <Grid container spacing={3}>
              <Grid item xs={12} lg={7}>
                <Stack spacing={3}>
                  <Paper
                    sx={{
                      p: 3,
                      border: "1px solid #e0e0e0",
                      borderRadius: 4,
                      backgroundColor: "#ffffff",
                      boxShadow: "none !important",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <i
                        className="ph ph-calculator"
                        style={{ fontSize: 20, color: "#1976d2" }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Base para Cálculo de Venda
                      </Typography>
                    </Box>

                    <RadioGroup
                      value={tipoBuscaBaseVenda}
                      onChange={handleRadioChange}
                      row
                    >
                      <FormControlLabel
                        id="PrCompra"
                        value="PrCompra"
                        control={<Radio size="small" />}
                        label="Preço Compra"
                      />
                      <FormControlLabel
                        id="PrStandard"
                        value="PrStandard"
                        control={<Radio size="small" />}
                        label="Preço Standard"
                      />
                      <FormControlLabel
                        id="CustoMedio"
                        value="CustoMedio"
                        control={<Radio size="small" />}
                        label="Custo Médio"
                      />
                    </RadioGroup>
                  </Paper>
                  <Paper
                    sx={{
                      p: 3,
                      border: "1px solid #e0e0e0",
                      borderRadius: 4,
                      backgroundColor: "#ffffff",
                      boxShadow: "none !important",
                    }}
                  >
                    <Box display="flex" alignItems="center" gap={1} mb={3}>
                      <i
                        className="ph ph-info"
                        style={{ fontSize: 20, color: "#1976d2" }}
                      />
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Informações Básicas
                      </Typography>
                    </Box>

                    <Grid container spacing={2}>
                      <Grid item xs={12} sm={6} md={3}>
                        <CurrencyInput
                          label="Preço Compra"
                          name="precoCompra"
                          value={inputValues.precoCompra || ""}
                          onBlur={(e) =>
                            handleChangeInput("precoCompra", e.target.value)
                          }
                          fullWidth
                          size="normal"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <CurrencyInput
                          label="Preço Standard"
                          name="precoStandard"
                          value={inputValues.precoStandard || ""}
                          onBlur={(e) =>
                            handleChangeInput("precoStandard", e.target.value)
                          }
                          fullWidth
                          size="normal"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <CurrencyInput
                          label="Preço Médio"
                          name="precoMedio"
                          value={inputValues.precoMedio || ""}
                          onBlur={(e) =>
                            handleChangeInput("precoMedio", e.target.value)
                          }
                          fullWidth
                          size="normal"
                          variant="outlined"
                        />
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <TextFielDecimalNumbers
                          label="Lucro Desejado (%)"
                          name="lucroDesejado"
                          value={inputValues.lucroDesejado || ""}
                          onBlur={(e) =>
                            handleChangeInput("lucroDesejado", e.target.value)
                          }
                          fullWidth
                          size="normal"
                          variant="outlined"
                          inputProps={{ min: 0, max: 99.99 }}
                        />
                      </Grid>
                    </Grid>
                  </Paper>

                  {parametrosCompra.length > 0 && (
                    <Paper
                      sx={{
                        p: 3,
                        border: "0.5px solid #e0e0e0",
                        borderRadius: 4,
                        backgroundColor: "#ffffff",
                        boxShadow: "none !important",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={3}>
                        <i
                          className="ph ph-shopping-cart"
                          style={{ fontSize: 20, color: "#1976d2" }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Incidencia sobre o{" "}
                          {
                            BASECALCULOS.find(
                              (base) => base.name === tipoBuscaBaseVenda
                            ).value
                          }
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {parametrosCompra
                          .sort((a, b) => a.ordem - b.ordem)
                          .map((param) => (
                            <Grid item xs={12} sm={6} md={3} key={param.id}>
                              {param.tipo === "DECIMAL" ? (
                                <CurrencyInput
                                  label={param.descricao}
                                  name={param.identificador}
                                  value={inputValues[param.identificador] || ""}
                                  disabled={param?.calculoSemParametro}
                                  onBlur={(e) =>
                                    handleChangeInput(
                                      param.identificador,
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                  size="normal"
                                  variant="outlined"
                                />
                              ) : (
                                <TextFielDecimalNumbers
                                  label={`${param.descricao} (%)`}
                                  name={param.identificador}
                                  value={inputValues[param.identificador] || ""}
                                  disabled={param?.calculoSemParametro}
                                  onBlur={(e) =>
                                    handleChangeInput(
                                      param.identificador,
                                      e.target.value
                                    )
                                  }
                                  fullWidth
                                  size="normal"
                                  variant="outlined"
                                  inputProps={{ min: 0, max: 100 }}
                                />
                              )}
                            </Grid>
                          ))}
                      </Grid>
                    </Paper>
                  )}

                  {parametrosVenda.length > 0 && (
                    <Paper
                      sx={{
                        p: 3,
                        border: "1px solid #e0e0e0",
                        borderRadius: 4,
                        backgroundColor: "#ffffff",
                        boxShadow: "none !important",
                      }}
                    >
                      <Box display="flex" alignItems="center" gap={1} mb={3}>
                        <i
                          className="ph ph-tag"
                          style={{ fontSize: 20, color: "#1976d2" }}
                        />
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          Incidencia sobre a Venda
                        </Typography>
                      </Box>

                      <Grid container spacing={2}>
                        {parametrosVenda.map((param) => (
                          <Grid item xs={12} sm={6} md={3} key={param.id}>
                            <TextFielDecimalNumbers
                              label={`${param.descricao} (%)`}
                              name={param.identificador}
                              value={inputValues[param.identificador] || ""}
                              onBlur={(e) =>
                                handleChangeInput(
                                  param.identificador,
                                  e.target.value
                                )
                              }
                              fullWidth
                              margin="small"
                              size="normal"
                              variant="outlined"
                              inputProps={{ min: 0, max: 100 }}
                            />
                          </Grid>
                        ))}
                        <Grid item xs={12} sm={6} md={3}>
                          <TextFielDecimalNumbers
                            label={"COMISSÃO (%)"}
                            name="comissao"
                            value={inputValues["comissao"] || ""}
                            onBlur={(e) =>
                              handleChangeInput("comissao", e.target.value)
                            }
                            fullWidth
                            margin="small"
                            size="normal"
                            variant="outlined"
                            inputProps={{ min: 0, max: 100 }}
                          />
                        </Grid>
                      </Grid>
                    </Paper>
                  )}
                </Stack>
                <Box sx={{ mt: 3 }}>
                  <Accordion
                    expanded={expandedLog}
                    onChange={handleLogAccordionChange}
                    sx={{
                      border: "1px solid #e0e0e0",
                      borderRadius: 4,
                      boxShadow: "none !important",
                      "&:before": { display: "none" },
                    }}
                  >
                    <AccordionSummary
                      expandIcon={<i className="ph ph-caret-down" />}
                      sx={{ backgroundColor: "#ffffff" }}
                    >
                      <Typography variant="h6" sx={{ fontWeight: 600 }}>
                        Log de Cálculos
                      </Typography>
                    </AccordionSummary>
                    <AccordionDetails sx={{ p: 3 }}>
                      {logCalculos.map((section, sectionIdx) => (
                        <Box key={sectionIdx} mb={3}>
                          <Typography
                            variant="subtitle1"
                            sx={{ fontWeight: 600, mb: 1 }}
                          >
                            {section.section}
                          </Typography>
                          {section.steps.map((step, stepIdx) => (
                            <Typography
                              key={stepIdx}
                              variant="body2"
                              sx={{ mb: 0.5, fontFamily: "monospace" }}
                            >
                              {step}
                            </Typography>
                          ))}
                        </Box>
                      ))}
                    </AccordionDetails>
                  </Accordion>
                </Box>
              </Grid>

              <Grid item xs={12} lg={5}>
                <Paper
                  sx={{
                    p: 3,
                    backgroundColor: "#f8f9fa",
                    border: "1px solid #e0e0e0",
                    borderRadius: 4,
                    position: "sticky",
                    top: 20,
                    minHeight: "36.3rem",
                  }}
                >
                  <Box display="flex" alignItems="center" gap={1} mb={2}>
                    <i
                      className="ph ph-calculator"
                      style={{ fontSize: 20, color: "#1976d2" }}
                    />
                    <Typography
                      variant="h6"
                      sx={{ fontWeight: 600, color: "#1a1a1a" }}
                    >
                      Resumo da Precificação
                    </Typography>
                  </Box>

                  <Box>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 500, color: "#666" }}
                    >
                      Valores Anteriores
                    </Typography>
                    <Stack spacing={1}>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Preço de Custo:
                        </Typography>
                        <Box
                          sx={{
                            borderBottom: "1px dotted grey",
                            borderWidth: "0.170rem",
                            flexGrow: "2",
                            mx: "1rem",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mr: 1 }}
                        >
                          {formatPrice(currentItem?.precoCusto || 0, false)}
                        </Typography>
                      </Box>
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" color="text.secondary">
                          Preço de Venda:
                        </Typography>
                        <Box
                          sx={{
                            borderBottom: "1px dotted grey",
                            borderWidth: "0.170rem",
                            flexGrow: "2",
                            mx: "1rem",
                          }}
                        />
                        <Typography
                          variant="body2"
                          sx={{ fontWeight: 600, mr: 1 }}
                        >
                          {formatPrice(currentItem?.precoVenda || 0, false)}
                        </Typography>
                      </Box>
                    </Stack>
                  </Box>
                  <Divider sx={{ my: 2 }} />
                  <Box>
                    <Typography
                      variant="subtitle2"
                      gutterBottom
                      sx={{ fontWeight: 500, color: "#1976d2", pb: 1 }}
                    >
                      Valores Sugeridos
                    </Typography>
                    <Stack spacing={2}>
                      <Incidencias parametros={compraParamValues} />
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Preço de Custo (Sugerido):
                        </Typography>
                        <Box
                          sx={{
                            borderBottom: "1px dotted grey",
                            borderWidth: "0.170rem",
                            flexGrow: "2",
                            mx: "1rem",
                          }}
                        />
                        <Chip
                          label={
                            precificacaoCalculada.precoCusto
                              ? formatPrice(
                                  precificacaoCalculada.precoCusto,
                                  false
                                )
                              : "0,00"
                          }
                          size="small"
                          color="primary"
                          sx={{ fontFamily: "monospace", fontWeight: 600 }}
                        />
                      </Box>
                      <Incidencias parametros={vendaParamValues} venda />
                      <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                      >
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Preço de Venda (Sugerido):
                        </Typography>
                        <Box
                          sx={{
                            borderBottom: "1px dotted grey",
                            borderWidth: "0.170rem",
                            flexGrow: "2",
                            mx: "1rem",
                          }}
                        />
                        {editando ? (
                          <CurrencyInput
                            name="precoVenda"
                            value={precificacaoCalculada?.precoVenda || ""}
                            fullWidth
                            size="small"
                            variant="outlined"
                            onKeyDown={(e) => {
                              if (e.key === "Enter") {
                                editPrecoVenda(e);
                              }
                            }}
                            onBlur={(e) => editPrecoVenda(e)}
                            InputProps={{
                              sx: {
                                width: 10,
                                fontFamily: "monospace",
                                fontWeight: 600,
                                height: 28,
                              },
                            }}
                          />
                        ) : (
                          <>
                            <Chip
                              label={
                                temErroCalculo
                                  ? "Erro"
                                  : formatPrice(
                                      precificacaoCalculada?.precoVenda ?? 0,
                                      false
                                    )
                              }
                              size="small"
                              color={temErroCalculo ? "error" : "success"}
                              sx={{ fontFamily: "monospace", fontWeight: 600 }}
                              onClick={() => setEditando(true)}
                              onDelete={() => setEditando(true)}
                              deleteIcon={
                                <i
                                  className="ph-fill ph-pencil"
                                  style={{
                                    color: "white",
                                    paddingInline: 5,
                                    fontSize: 12,
                                  }}
                                ></i>
                              }
                            />
                          </>
                        )}
                      </Box>
                    </Stack>
                  </Box>

                  {!!temErroCalculo && (
                    <Typography
                      variant="caption"
                      display="block"
                      sx={{ mt: 2, color: "error.main" }}
                    >
                      Verifique os percentuais de venda. A soma pode estar igual
                      ou superior a 100%.
                    </Typography>
                  )}

                  {!!temErroCalculo &&
                    precificacaoCalculada.precoVenda &&
                    precificacaoCalculada.precoCusto && (
                      <Box sx={{ mt: 2 }}>
                        <Typography
                          variant="subtitle2"
                          gutterBottom
                          sx={{ fontWeight: 500, color: "#2e7d32" }}
                        >
                          Análise de Margem
                        </Typography>
                        <Stack spacing={1}>
                          {(() => {
                            const margem =
                              precificacaoCalculada.precoVenda -
                              precificacaoCalculada.precoCusto;
                            const percentualMargem =
                              precificacaoCalculada.precoVenda > 0
                                ? (margem / precificacaoCalculada.precoVenda) *
                                  100
                                : 0;
                            const markup =
                              precificacaoCalculada.precoCusto > 0
                                ? (margem / precificacaoCalculada.precoCusto) *
                                  100
                                : 0;
                            return (
                              <>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Margem (R$):
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {formatPrice(margem, false)}
                                  </Typography>
                                </Box>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Margem (%):
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {percentualMargem.toFixed(2)}%
                                  </Typography>
                                </Box>
                                <Box
                                  display="flex"
                                  justifyContent="space-between"
                                  alignItems="center"
                                >
                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                  >
                                    Markup:
                                  </Typography>
                                  <Typography
                                    variant="caption"
                                    sx={{
                                      fontFamily: "monospace",
                                      fontWeight: 500,
                                    }}
                                  >
                                    {markup.toFixed(2)}%
                                  </Typography>
                                </Box>
                              </>
                            );
                          })()}
                        </Stack>
                      </Box>
                    )}
                </Paper>
              </Grid>
            </Grid>
          </CardContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button onClick={handleClose} variant="contained" color="padrao">
              Cancelar
            </Button>
            <Button
              onClick={(e) => onSubmit(e)}
              variant="contained"
              disabled={loading || temErroCalculo}
              startIcon={loading && <Loading size={16} />}
            >
              {loading ? "Salvando..." : "Salvar Precificação"}
            </Button>
          </DialogActions>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default ModalPrecificacao;
